import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios'
import { getSession, signIn } from 'next-auth/react'
import { getApiConfig } from '../utils'
import { shouldAttemptSignIn } from '../auth/sign-in-guard'

/**
 * Named axios instance so auth interceptors are scoped to backend API calls
 * only and do not interfere with other axios usage in the codebase.
 */
const apiAxios: AxiosInstance = axios.create()

/**
 * Short-lived, single-flight in-memory session cache.
 *
 * getSession() hits /api/auth/session on every call, which runs the NextAuth
 * jwt callback server-side. Two layers of deduplication keep that under
 * control:
 *
 *  1. Result cache (10s TTL) — once we have a session, reuse it.
 *  2. In-flight de-dup — if a fetch is already running, every concurrent
 *     caller awaits the same Promise instead of issuing its own request.
 *
 * Without (2), a page mount that fires N parallel API calls produces N
 * parallel /api/auth/session requests (and N concurrent runs of the jwt
 * callback, which can mean N concurrent refresh-token exchanges with Azure).
 * The single-flight collapses that burst into a single fetch.
 */
type CachedSession = Awaited<ReturnType<typeof getSession>>

let cachedSession: CachedSession = null
let sessionCacheExpiresAt = 0
let inflightSessionFetch: Promise<CachedSession> | null = null
const SESSION_CACHE_TTL_MS = 10_000

async function getCachedSession(): Promise<CachedSession> {
  if (Date.now() < sessionCacheExpiresAt && cachedSession !== null) {
    return cachedSession
  }
  if (inflightSessionFetch) {
    return inflightSessionFetch
  }
  inflightSessionFetch = getSession()
    .then((session) => {
      cachedSession = session
      sessionCacheExpiresAt = Date.now() + SESSION_CACHE_TTL_MS
      return session
    })
    .finally(() => {
      inflightSessionFetch = null
    })
  return inflightSessionFetch
}

/**
 * Clears the in-memory session cache. Used internally before re-auth so the
 * next outbound request fetches a fresh session, and exported for tests.
 *
 * Note: this clears the result cache but does NOT abort an in-flight fetch.
 * Any callers already awaiting the in-flight Promise will still receive its
 * result; subsequent callers will see the empty cache and start a new fetch.
 */
export function invalidateSessionCache() {
  cachedSession = null
  sessionCacheExpiresAt = 0
  inflightSessionFetch = null
}

/**
 * Triggers a re-auth, but only if the loop guard allows it. Centralised here
 * so the request and response interceptors share the same debounce window —
 * a 401 followed immediately by a RefreshAccessTokenError must not fire two
 * sign-ins back to back.
 *
 * Exported for test use; consumed via the interceptors below in production.
 */
export async function triggerReAuth(reason: string): Promise<void> {
  invalidateSessionCache()
  if (shouldAttemptSignIn()) {
    await signIn('azure-ad', {
      callbackUrl:
        typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
    })
    return
  }
  console.error(
    `[api-client] Sign-in loop guard tripped (${reason}); not re-authenticating. ` +
      'This usually indicates a backend token-validation misconfiguration.'
  )
}

/**
 * Request interceptor: attach the Azure AD id_token from the NextAuth session
 * as the Bearer token on every outbound request.
 *
 * If the server-side token refresh failed (RefreshAccessTokenError), force a
 * full re-authentication rather than sending an expired/missing token.
 *
 * Exported as a named function so it can be unit-tested directly; the real
 * axios wiring is done at module load below.
 */
export async function authRequestInterceptor(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  const session = await getCachedSession()

  if (session?.error === 'RefreshAccessTokenError') {
    await triggerReAuth('RefreshAccessTokenError')
    return Promise.reject(new Error('Token refresh failed; redirecting to sign-in.'))
  }

  if (session?.idToken) {
    config.headers = AxiosHeaders.from(config.headers).set('Authorization', `Bearer ${session.idToken}`)
  }

  return config
}

/**
 * Response interceptor for failures: any 401 from the backend means the access
 * token was rejected. Force re-authentication, subject to the loop guard so we
 * don't bounce the user to Azure forever if the backend keeps rejecting tokens.
 */
export async function authResponseErrorInterceptor(error: AxiosError): Promise<never> {
  if (error.response?.status === 401) {
    await triggerReAuth('401-from-backend')
  }
  return Promise.reject(error)
}

apiAxios.interceptors.request.use(authRequestInterceptor)
apiAxios.interceptors.response.use((response) => response, authResponseErrorInterceptor)

export async function getApiClient({
  endpoint = '',
  rootUri = '',
  params = {},
  config = {} as AxiosRequestConfig,
}) {
  const apiConfig = getApiConfig()
  return apiAxios
    .get(`${rootUri || apiConfig.apiRootUri}${endpoint}`, { params, ...config })
    .then((response) => response.data)
    .catch((error: unknown) => {
      let normalizedError: ApiErrorResponse = {
        message: 'An unexpected error occurred',
        statusCode: 500,
      }
      if (axios.isAxiosError(error) && error.response) {
        normalizedError = normalizeError(error)
      } else if (error instanceof Error) {
        normalizedError.message = error.message
      }
      console.error('Error:', normalizedError)
      return Promise.reject(normalizedError)
    })
}

export async function postApiClient({
  endpoint = '',
  rootUri = '',
  payload = {},
  config = {} as AxiosRequestConfig,
  params = {},
}) {
  const apiConfig = getApiConfig()
  return apiAxios
    .post(`${rootUri || apiConfig.apiRootUri}${endpoint}`, payload, { params, ...config })
    .then((response) => response.data)
    .catch((error: unknown) => {
      let normalizedError: ApiErrorResponse = {
        message: 'An unexpected error occurred',
        statusCode: 500,
      }
      if (axios.isAxiosError(error) && error.response) {
        normalizedError = normalizeError(error)
      } else if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        normalizedError = {
          message: 'The request took too long. Please try again.',
          statusCode: 408,
          isTimeout: true,
        }
      } else if (error instanceof Error) {
        normalizedError.message = error.message
      }
      console.error('Error:', normalizedError)
      return Promise.reject(normalizedError)
    })
}

export async function putApiClient({
  endpoint = '',
  rootUri = '',
  payload = {},
  config = {} as AxiosRequestConfig,
  params = {},
}) {
  const apiConfig = getApiConfig()
  return apiAxios
    .put(`${rootUri || apiConfig.apiRootUri}${endpoint}`, null, { params, ...config })
    .then((response) => response.data)
    .catch((error: unknown) => {
      let normalizedError: ApiErrorResponse = {
        message: 'An unexpected error occurred',
        statusCode: 500,
      }
      if (axios.isAxiosError(error) && error.response) {
        normalizedError = normalizeError(error)
      } else if (error instanceof Error) {
        normalizedError.message = error.message
      }
      console.error('Error:', normalizedError)
      return Promise.reject(normalizedError)
    })
}

export async function deleteApiClient({
  endpoint = '',
  rootUri = '',
  payload = {},
  config = {} as AxiosRequestConfig,
  params = {},
}) {
  const apiConfig = getApiConfig()
  return apiAxios
    .delete(`${rootUri || apiConfig.apiRootUri}${endpoint}`, { params, ...config })
    .then((response) => response.data)
    .catch((error: unknown) => {
      let normalizedError: ApiErrorResponse = {
        message: 'An unexpected error occurred',
        statusCode: 500,
      }
      if (axios.isAxiosError(error) && error.response) {
        normalizedError = normalizeError(error)
      } else if (error instanceof Error) {
        normalizedError.message = error.message
      }
      console.error('Error:', normalizedError)
      return Promise.reject(normalizedError)
    })
}

interface ApiErrorResponse {
  message: string
  statusCode: number
  statusText?: string
  data?: any
  /** Set when axios aborted the request (typically timeout). */
  isTimeout?: boolean
}

const normalizeError = (error: AxiosError): ApiErrorResponse => {
  const backendError = error.response?.data as { error: { message?: string } }
  return {
    message: backendError?.error?.message || error.message || 'An error occurred',
    statusCode: error.response?.status || 500,
    statusText: error.response?.statusText,
    data: error.response?.data,
  }
}
